import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as Isotope from 'isotope-layout';
import { NavigationService } from '../navigation.service';
import { LayoutService } from '../layout.service';
import { staticInOut } from '../router.animations';
import { BreakpointsService } from '../breakpoints.service';
import { Subscription } from 'rxjs/Subscription';
import { appConfig } from './../app.config';
import * as imagesLoaded from 'imagesloaded';
import { find, findIndex, uniqBy, orderBy, maxBy, minBy, concat, slice, forEach, last } from 'lodash';

@Component({
  selector: 'app-grid-view',
  templateUrl: './grid-view.component.html',
  styleUrls: ['./grid-view.component.scss'],
  host: {
    '(window:resize)': 'onResize($event)',
    '[@staticInOut]': ''
  },
  animations: [
    staticInOut
  ]
})
export class GridViewComponent implements OnInit, OnDestroy {
  @Input() apiData;
  @Input() isLoadingApiData;
  @ViewChild('gridWrap') gridWrapRef: ElementRef;
  @ViewChild('grid') gridRef: ElementRef;
  @ViewChild('viewport') viewportRef: ElementRef;
  public imageSrcBase = appConfig.imageUrl;
  gridWrapEl;
  gridEl;
  viewportEl;
  gridDisplay: string;
  items: any[];
  loadedItems: any[];
  iso;
  hoverItem;
  hoverItemIndex: number;
  alignTextRight: boolean = false;
  textVisibility: string = 'hidden';
  itemHeight: number;
  numberOfItemsLoaded: number;
  orderBy: string;
  orderAscending: boolean;
  category: string;
  maxYear: number;
  minYear: number = null;
  animationStagger: number;
  chunksLoaded: number = 15;
  resizeTimeout: any;
  breakpointSubscription: Subscription;
  itemsInitialised: boolean = false;
  isIso: boolean = false;
  loadingImages: boolean = false;
  device: string;
  haveSetScrollTop = false;

  get scrollTop() {
    const docScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    return docScrollTop === 0 ? this.viewportEl.scrollTop : docScrollTop;
  }

  constructor(
    private NavigationService: NavigationService,
    private route: ActivatedRoute,
    private BreakpointsService: BreakpointsService,
    private LayoutService: LayoutService,
    private router: Router
  ) {
    this.router.routeReuseStrategy.shouldReuseRoute = function() {
      return false;
    };
  }

  ngOnInit() {
    this.orderBy = this.apiData.default_order_by || 'none';
    this.orderAscending = this.apiData.default_sort === 'asc' || null;
    this.items = orderBy(this.apiData.thumbs, ['position'], ['asc']);
    this.maxYear = new Date().getFullYear();

    // Getting date min & max
    if (this.items.length > 0) {
      let minDateProject = minBy(this.items, 'date');
      this.minYear = new Date(minDateProject.date).getFullYear();
    }

    this.gridEl = this.gridRef.nativeElement;
    this.gridWrapEl = this.gridWrapRef.nativeElement;
    this.viewportEl = this.viewportRef.nativeElement;

    this.breakpointSubscription = this.BreakpointsService.deviceObs.subscribe((device) => {
      if (!this.itemsInitialised) {
        this.initItems();
      }

      if (device !== 'desktop' && this.isIso) {
        this.destroyIso();
      }

      this.device = device;

      this.initGrid();
    });
  }

  ngOnDestroy() {
    this.LayoutService.saveGridScrollPosition(this.apiData.id, this.scrollTop);
    if (typeof this.breakpointSubscription !== 'undefined') {
      this.breakpointSubscription.unsubscribe();
    }
  }

  onInfiniteScroll() {
    if (!this.loadingImages && this.loadedItems.length !== this.items.length) {
      this.lazyLoadItems();
    }
  }

  showLoadingGif() {
    this.loadingImages = true;
  }
  
  trackItem(index, item) {
    return item ? item.id : undefined;
  }

  hideLoadingGif() {
    this.loadingImages = false;
  }

  sizeFix(_item, _imageEl) {
    setTimeout(() => {
      _item.naturalWidth = _imageEl.naturalWidth;
      _item.naturalHeight = _imageEl.naturalHeight;

      if (_item.naturalHeight === 0 || _item.naturalWidth === 0) {
        this.sizeFix(_item, _imageEl);
      }
    }, 100);
  }

  lazyLoadItems(callback = () => {}) {
    if (this.numberOfItemsLoaded === this.items.length) {
      return false;
    }

    this.showLoadingGif();

    let newNumberOfItemsLoaded = this.numberOfItemsLoaded + this.chunksLoaded;
    if (newNumberOfItemsLoaded > this.items.length) {
      newNumberOfItemsLoaded = this.items.length;
    }

    this.loadedItems = concat(this.loadedItems, slice(this.items, this.numberOfItemsLoaded, newNumberOfItemsLoaded));
    setTimeout(() => {
      let appendedElements: Element[] = slice<Element>(this.gridEl.getElementsByClassName('grid-item'), this.numberOfItemsLoaded, newNumberOfItemsLoaded);

      imagesLoaded(appendedElements, (event) => {
        forEach(event.images, (image, elIndex) => {
          let imageEl = image.img;
          let itemId = parseInt(imageEl.getAttribute('id'));

          setTimeout(() => {
            imageEl.classList.add('loaded');
          }, 500);

          let item = find(this.loadedItems, {id: itemId});
          item.naturalWidth = imageEl.naturalWidth;
          item.naturalHeight = imageEl.naturalHeight;

          if (item.naturalHeight === 0 || item.naturalWidth === 0) {
            this.sizeFix(item, imageEl);
          }
        });

        setTimeout(() => {
          if (this.isIso) {
            this.iso.appended(appendedElements);
          }

          for (let i = this.numberOfItemsLoaded; i < newNumberOfItemsLoaded; i++) {
            this.loadedItems[i].visibility = 'visible';
          }

          this.numberOfItemsLoaded = newNumberOfItemsLoaded;

          setTimeout(() => {
            this.hideLoadingGif();
            callback();
          }, this.animationStagger * this.chunksLoaded);
        });
      });
    }, 0);
  }

  onResize() {
    this.setItemHeight();

    setTimeout(() => {
      // img container div does not correctly resize in Chrome
      // Force Chrome UI Repaint
      this.gridDisplay = this.gridDisplay == 'block' ? 'inline-block' : 'block';
      
    });

    clearTimeout(this.resizeTimeout);

    this.resizeTimeout = setTimeout(() => {
      if (this.isIso) {
        this.iso.once('layoutComplete', (laidOutItems) => {
          this.fillScreen();
        });
      }
    }, 250);
  }

  setItemHeight() {
    let gridHeight = this.gridWrapEl.clientHeight;
    let itemHeight = gridHeight/4;
    this.itemHeight = itemHeight < 130 ? 130 : itemHeight;
  }

  initItems() {
    this.setItemHeight();

    // Set items to first this.numberOfItemsLoaded items
    if (this.orderBy !== 'none') {
      this.items = orderBy(this.items, ['date'], [this.orderAscending ? 'asc' : 'desc']);
    }

    forEach(this.items, (item) => {
      item.visibility = 'hidden';
    });

    this.numberOfItemsLoaded = 0;
    this.loadedItems = [];

    this.itemsInitialised = true;
  }

  initGrid() {
    setTimeout(() => {
      if (this.BreakpointsService.device === 'desktop' && !this.isIso) {
        this.isIso = true;
  
        let options = {
          itemSelector: '.grid-item',
          layoutMode: 'fitRows',
          fitRows: {
            gutter: 12
          },
          stagger: this.animationStagger,
          transitionDuration: 400
        }

        this.iso = new Isotope(this.gridEl, options);
      }
      if (this.NavigationService.isBack) {
        const scrollPosition = this.LayoutService.getGridScrollPosition(this.apiData.id);
        this.fillScreen(scrollPosition);
      } else {
        this.fillScreen();
      }
    });
  }

  destroyIso() {
    this.isIso = false;
    this.iso.destroy();
  }

  fillScreen(scrollTo = null) {
    let isScreenFilled;
    if (this.scrollTop > 0) {
      isScreenFilled = true;
    } else if (this.numberOfItemsLoaded > 0) {
      let lastItem: any = last(this.gridEl.getElementsByClassName('grid-item'));
      isScreenFilled = lastItem.getBoundingClientRect().top > window.innerHeight
    } else {
      isScreenFilled = false;
    }

    if (!isScreenFilled || (scrollTo && !this.haveSetScrollTop)) {
      this.lazyLoadItems(() => {
        this.fillScreen(scrollTo);
      });
    }

    setTimeout(() => {
      document.body.scrollTop = scrollTo;
      document.documentElement.scrollTop = scrollTo;
      this.viewportEl.scrollTop = scrollTo;
      this.haveSetScrollTop = this.scrollTop === scrollTo;
    });
  }

  reorderGrid() {
    this.orderAscending = !this.orderAscending;

    if (this.isIso) {
      this.destroyIso();
    }

    this.initItems();

    this.initGrid();
  }

  onItemClick(uid) {
    // Need to save thumbs array reordered to start at current thumb
    // so that we know the navigation order when using arrows
    // on project page.
    // Otherwise if navigate from grid view to project
    // then navigate to next project
    // will go to project for thumb which is after FIRST thumb for the project
    // instead of after actual thumb selected
    let index = findIndex(this.items, {id: uid});
    let thumbs = slice(this.items, index, this.items.length).concat(slice(this.items, 0, index));
    thumbs = uniqBy(thumbs, 'page_id');
    this.NavigationService.projectThumbs = thumbs;
  }

  onMouseenter($event, index) {
    let itemEl = $event.target;
    let item = this.loadedItems[index];
    this.textVisibility = 'hidden';
    this.hoverItemIndex = index;
    this.hoverItem = item;

    setTimeout(() => {
      let textBounding = itemEl.getElementsByClassName('text-inner')[0].getBoundingClientRect();
      if (textBounding.width === 0) {
        return;
      }
      let gridBounding = this.gridEl.getBoundingClientRect();
      if (textBounding.right > gridBounding.right) {
        this.alignTextRight = true;
      }
      this.textVisibility = 'visible';
    });
  }

  onMouseleave($event, index) {
    let itemEl = $event.target;
    let item = this.loadedItems[index];
    this.textVisibility = 'hidden';
    this.hoverItemIndex = null;
    this.hoverItem = null;
    this.alignTextRight = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/grid-view/grid-view.component.ts